<template>
  <div class="api-playground">
    <Sidebar />
    
    <div class="main-content">
      <header class="playground-header">
        <h1 class="playground-title">Playground</h1>
      </header>

      <div class="playground-container">
        <!-- Configuration Panel -->
        <div class="config-panel">
          <div class="config-section">
            <label class="config-label">Query <span class="required">q</span></label>
            <input 
              v-model="config.query" 
              type="text" 
              class="config-input"
              placeholder="Enter search query..."
              @keyup.enter="executeSearch"
            />
          </div>

          <div class="config-row">
            <div class="config-col">
              <label class="config-label">Count</label>
              <select v-model="config.count" class="config-select compact">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
            <div class="config-col">
              <label class="config-label">Freshness</label>
              <select v-model="config.freshness" class="config-select compact">
                <option value="">Any time</option>
                <option value="Day">Day</option>
                <option value="Week">Week</option>
                <option value="Month">Month</option>
              </select>
            </div>
          </div>

          <div class="config-section">
            <label class="config-label">Offset <span class="optional">{{ config.offset }}</span></label>
            <input 
              type="range" 
              v-model.number="config.offset" 
              min="0" 
              max="100" 
              step="1"
              class="offset-slider"
            />
          </div>

          <div class="config-section endpoint-section">
            <label class="config-label">API Endpoint</label>
            <input 
              v-model="config.endpoint" 
              type="text" 
              class="config-input"
              placeholder="Enter endpoint..."
            />
          </div>

          <div class="config-section">
            <label class="config-label">API Key (AK)</label>
            <input 
              v-model="config.apiKey" 
              type="password" 
              class="config-input"
              placeholder="Enter your API key..."
            />
          </div>

          <div class="config-footer">
            <button @click="executeSearch" class="search-btn" :disabled="isLoading">
              {{ isLoading ? 'Searching...' : 'Search' }}
            </button>
          </div>
        </div>

        <!-- Results Panel -->
        <div class="results-panel">
          <div class="results-header">
            <div class="tab-controls">
              <button 
                @click="activeTab = 'results'" 
                :class="['tab-btn', { active: activeTab === 'results' }]"
              >
                Results
              </button>
              <button 
                @click="activeTab = 'code'" 
                :class="['tab-btn', { active: activeTab === 'code' }]"
              >
                Code
              </button>
            </div>
            <div class="request-controls" v-if="activeTab === 'code'">
              <button @click="copyResponse" class="copy-btn">Copy JSON</button>
            </div>
          </div>

          <div class="results-content">
            <div v-if="activeTab === 'results'" class="results-tab">
              <div v-if="isLoading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Searching...</p>
              </div>
              <div v-else-if="searchResults" class="search-results">
                <div class="results-info">
                  <p>Found {{ searchResults.webPages?.value?.length || 0 }} results for "{{ searchResults.queryContext?.originalQuery }}"</p>
                </div>
                <div class="results-list">
                  <div 
                    v-for="(result, index) in searchResults.webPages?.value || []" 
                    :key="index"
                    class="result-item"
                  >
                    <h3 class="result-title">
                      <a :href="result.url" target="_blank" rel="noopener">{{ result.name }}</a>
                    </h3>
                    <p class="result-url">{{ result.displayUrl || result.url }}</p>
                    <p class="result-snippet">{{ result.snippet }}</p>
                    <div class="result-meta">
                      <span v-if="result.siteName" class="site-name">{{ result.siteName }}</span>
                      <span v-if="result.datePublished" class="publish-date">{{ formatDate(result.datePublished) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="error" class="error-state">
                <div class="error-icon">⚠️</div>
                <h3>Error</h3>
                <p>{{ error }}</p>
              </div>
              <div v-else class="empty-state">
                <div class="empty-icon">🔍</div>
                <h3>Ready to search</h3>
                <p>Configure your search parameters and click "Search" to get started.</p>
              </div>
            </div>

            <div v-if="activeTab === 'code'" class="code-tab">
              <div v-if="searchResults" class="json-response">
                <pre class="code-block"><code>{{ JSON.stringify(searchResults, null, 2) }}</code></pre>
              </div>
              <div v-else class="empty-state">
                <div class="empty-icon">📄</div>
                <h3>No response yet</h3>
                <p>Execute a search to see the JSON response here.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import Sidebar from '@/components/Sidebar.vue'

export default {
  name: 'SearchPlayground',
  components: {
    Sidebar
  },
  setup() {
    const activeTab = ref('results')
    const isLoading = ref(false)
    const searchResults = ref(null)
    const error = ref(null)

    const config = reactive({
      type: 'search',
      query: '',
      count: 10,
      freshness: '',
      setLang: '',
      endpoint: '',
      apiKey: '',
      offset: 0
    })

    const executeSearch = async () => {
      if (!config.query.trim()) {
        error.value = 'Query is required'
        return
      }

      if (!config.endpoint.trim()) {
        error.value = 'API Endpoint is required'
        return
      }

      if (!config.apiKey.trim()) {
        error.value = 'API Key is required'
        return
      }

      isLoading.value = true
      error.value = null
      searchResults.value = null

      try {
        const params = new URLSearchParams()
        params.append('q', config.query)
        params.append('count', config.count)
        params.append('offset', config.offset)
        if (config.setLang) params.append('setLang', config.setLang)
        if (config.freshness) params.append('freshness', config.freshness)

        const url = `/search-proxy/search/${config.endpoint}/smart?${params.toString()}`
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${config.apiKey}`,
            'pragma': 'no-cache'
          },
          credentials: 'omit'
        })

        let data
        try {
          data = await response.json()
        } catch (jsonError) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        if (!response.ok) {
          // For 4xx errors, try to get the specific error message from response
          if (response.status >= 400 && response.status < 500) {
            if (data.error && data.error.message) {
              throw new Error(data.error.message)
            }
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        // 检查是否有错误响应
        if (data.error) {
          if (data.error.code === "1225") {
            error.value = "Not enough balance"
          } else {
            error.value = data.error.message || 'An error occurred while searching'
          }
          return
        }
        
        searchResults.value = data
      } catch (err) {
        error.value = err.message || 'An error occurred while searching'
      } finally {
        isLoading.value = false
      }
    }


    const copyResponse = async () => {
      try {
        if (searchResults.value) {
          await navigator.clipboard.writeText(JSON.stringify(searchResults.value, null, 2))
          // Could add a toast notification here
        }
      } catch (err) {
        console.error('Failed to copy response:', err)
      }
    }

    const formatDate = (dateString) => {
      try {
        return new Date(dateString).toLocaleDateString()
      } catch {
        return dateString
      }
    }

    return {
      activeTab,
      isLoading,
      searchResults,
      error,
      config,
      executeSearch,
      copyResponse,
      formatDate
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.api-playground {
  height: 100vh;
  display: flex;
  background: #f8fafc;
  color: #1f2937;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 100vh;
  overflow: hidden;
}

.playground-header {
  display: flex;
  align-items: center;
  padding: 24px 32px 24px 80px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}

.playground-title {
  font-size: 32px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.playground-container {
  flex: 1;
  display: flex;
  gap: 1px;
  background: #e5e7eb;
  overflow: hidden;
}

.config-panel {
  width: 380px;
  background: #fff;
  padding: 24px;
  overflow-y: auto;
  border-right: 1px solid #e5e7eb;
  height: 100%;
}

.config-footer {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.config-section {
  margin-bottom: 20px;

  &.endpoint-section {
    border-top: 1px solid #e5e7eb;
    padding-top: 20px;
    margin-top: 24px;
  }
}

.config-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.config-col {
  flex: 1;
}

.config-label {
  display: block;
  font-size: 14px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  margin-bottom: 8px;
  color: #374151;

  .required {
    color: #10b981;
    font-size: 12px;
    font-weight: normal;
  }

  .optional {
    color: #6b7280;
    font-size: 12px;
    font-weight: normal;
  }
}

.input-group {
  display: flex;
  gap: 12px;
}

.config-input,
.config-select {
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #374151;
  font-size: 14px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:focus {
    outline: none;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &.compact {
    padding: 10px 14px 10px 14px;
    padding-right: 40px;
    font-size: 13px;
  }
}

.config-select {
  padding-right: 40px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

.search-btn {
  padding: 14px 40px;
  background: linear-gradient(135deg, #4285f4 0%, #3367d6 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3367d6 0%, #2563eb 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66, 133, 244, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0px);
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
}

.offset-slider {
  width: 100%;
  height: 6px;
  background: linear-gradient(to right, #f1f5f9, #e2e8f0);
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #4285f4, #3367d6);
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(66, 133, 244, 0.4);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 3px 10px rgba(66, 133, 244, 0.5);
    }
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #4285f4, #3367d6);
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(66, 133, 244, 0.4);
  }
}

.results-panel {
  flex: 1;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.tab-controls {
  display: flex;
  gap: 2px;
}

.tab-btn {
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;

  &.active {
    color: #4285f4;
    background: rgba(66, 133, 244, 0.1);
  }

  &:hover:not(.active) {
    color: #374151;
    background: #f3f4f6;
  }
}

.request-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}


.copy-btn {
  padding: 10px 18px;
  background: #fff;
  border: none;
  border-radius: 8px;
  color: #4285f4;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(66, 133, 244, 0.2);

  &:hover {
    background: #4285f4;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 6px rgba(66, 133, 244, 0.2);
  }
}

.results-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.results-tab,
.code-tab {
  padding: 32px;
  flex: 1;
  overflow-y: auto;
  min-width: 0;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #4285f4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.results-info {
  margin-bottom: 24px;
  color: #6b7280;
  font-size: 14px;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  padding: 24px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
}

.result-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 500;

  a {
    color: #4285f4;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.result-url {
  color: #10b981;
  font-size: 14px;
  margin: 0 0 12px 0;
}

.result-snippet {
  color: #374151;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.result-meta {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #6b7280;
}

.code-block {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  overflow-x: auto;
  white-space: pre-wrap;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 1200px) {
  .config-panel {
    width: 320px;
  }
}

@media (max-width: 768px) {
  .playground-container {
    flex-direction: column;
  }

  .config-panel {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #2a2a2a;
  }

  .playground-header {
    padding: 16px 20px;
  }

  .playground-title {
    font-size: 24px;
  }

  .results-header {
    padding: 16px 20px;
  }

  .results-tab,
  .code-tab {
    padding: 20px;
  }
}
</style>